import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Newspaper, Search, Filter, Users, TrendingUp, BarChart3,
  Instagram, Globe, DollarSign, MapPin, Star, Eye, Send,
  MessageSquare, ChevronRight, X, Check, Clock, Package,
  Loader2, ExternalLink, Heart, Sparkles, FileText, CreditCard,
  CheckCircle, AlertCircle, Play, Image as ImageIcon, XCircle, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';
import ContractChat from '../../components/ContractChat';
import NotificationBell from '../../components/NotificationBell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Image with fallback component
const ImageWithFallback = ({ src, alt, className, fallbackIcon: FallbackIcon = ImageIcon }) => {
  const [error, setError] = useState(false);
  
  if (error || !src) {
    return (
      <div className={`flex flex-col items-center justify-center bg-zinc-800 text-zinc-500 ${className}`}>
        <FallbackIcon size={24} className="mb-1 opacity-50" />
        <span className="text-xs text-center px-1">Imagen no disponible</span>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
    />
  );
};

const MEDIA_TYPES = [
  { value: 'all', label: 'Todos los tipos' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'multi-platform', label: 'Multi-plataforma' },
  { value: 'blog', label: 'Blog/Web' },
  { value: 'youtube', label: 'YouTube' },
];

// Service categories for filtering
const SERVICE_CATEGORIES = [
  { value: 'all', label: 'Todas las categorías' },
  { value: 'influencer', label: 'Influencer / Creador de Contenido', icon: 'sparkles' },
  { value: 'photographer', label: 'Fotógrafo/a', icon: 'camera' },
  { value: 'videographer', label: 'Camarógrafo/a / Videógrafo', icon: 'video' },
  { value: 'digital_media', label: 'Medio Digital / Blog', icon: 'globe' },
  { value: 'journalist', label: 'Periodista / Comunicador', icon: 'newspaper' },
  { value: 'radio', label: 'Radio / Podcast', icon: 'radio' },
  { value: 'dj_producer', label: 'DJ / Productor Musical', icon: 'music' },
  { value: 'social_media_manager', label: 'Community Manager', icon: 'share' },
  { value: 'graphic_designer', label: 'Diseñador/a Gráfico', icon: 'palette' },
  { value: 'event_coverage', label: 'Cobertura de Eventos', icon: 'calendar' },
  { value: 'marketing', label: 'Marketing / Publicidad', icon: 'megaphone' },
  { value: 'other', label: 'Otro', icon: 'more-horizontal' }
];

const SORT_OPTIONS = [
  { value: 'monthly_reach', label: 'Mayor alcance' },
  { value: 'completed_works', label: 'Más trabajos realizados' },
  { value: 'price', label: 'Menor precio' },
];

const BUDGET_RANGES = [
  { value: 'none', label: 'Sin especificar' },
  { value: '0-5000', label: 'Hasta $5.000' },
  { value: '5000-15000', label: '$5.000 - $15.000' },
  { value: '15000-30000', label: '$15.000 - $30.000' },
  { value: '30000-50000', label: '$30.000 - $50.000' },
  { value: '50000+', label: 'Más de $50.000' },
];

const ProducerPressPage = () => {
  const [searchParams] = useSearchParams();
  const [pressList, setPressList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    media_type: 'all',
    service_category: 'all',
    province: '',
    city: '',
    min_followers: '',
    max_price: '',
    sort_by: 'monthly_reach'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPress, setSelectedPress] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedQuoteForPayment, setSelectedQuoteForPayment] = useState(null);
  const [showOfferCheckoutModal, setShowOfferCheckoutModal] = useState(false);
  const [selectedOfferForPayment, setSelectedOfferForPayment] = useState(null);
  const [offerPaymentSummary, setOfferPaymentSummary] = useState(null);
  const [loadingOfferSummary, setLoadingOfferSummary] = useState(false);
  const [processingOfferPayment, setProcessingOfferPayment] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedContractForChat, setSelectedContractForChat] = useState(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
  const [selectedPressForPortfolio, setSelectedPressForPortfolio] = useState(null);
  const [myQuotes, setMyQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('browse'); // browse, quotes, offers, contracts
  const [offers, setOffers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [events, setEvents] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    event_id: '',
    package_ids: [],
    message: '',
    budget_range: ''
  });
  const [sendingQuote, setSendingQuote] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);

  // Platform fee percentage (default, can be overridden by press settings)
  const DEFAULT_PLATFORM_FEE_PERCENT = 10;

  // Calculate checkout amounts based on press settings
  const calculateCheckoutAmounts = (quote) => {
    if (!quote?.response?.quote_amount) return { serviceCost: 0, platformFee: 0, total: 0 };
    
    const quoteAmount = quote.response.quote_amount;
    const feePercent = quote.service_fee_percent || DEFAULT_PLATFORM_FEE_PERCENT;
    const pressAbsorbsFee = quote.press_absorbs_fee || false;
    
    if (pressAbsorbsFee) {
      // Press absorbs the fee - producer only pays quote amount
      return {
        serviceCost: quoteAmount,
        platformFee: 0, // Not shown to producer
        total: quoteAmount,
        showFee: false
      };
    } else {
      // Producer pays quote + fee
      const platformFee = Math.round(quoteAmount * feePercent / 100);
      return {
        serviceCost: quoteAmount,
        platformFee: platformFee,
        total: quoteAmount + platformFee,
        showFee: true
      };
    }
  };

  // Handle URL params for payment callbacks
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const payment = searchParams.get('payment');
    const contractId = searchParams.get('contract');
    
    if (tabParam) {
      setActiveTab(tabParam);
    }
    
    if (payment === 'success' && contractId) {
      toast.success('¡Pago realizado exitosamente! El contrato está activo.');
      fetchContracts();
    } else if (payment === 'failure') {
      toast.error('El pago no se pudo completar. Intentá nuevamente.');
    } else if (payment === 'pending') {
      toast.info('El pago está pendiente de confirmación.');
    }
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'browse') {
      fetchPress();
    } else if (activeTab === 'quotes') {
      fetchQuotes();
    } else if (activeTab === 'offers') {
      fetchOffers();
    } else if (activeTab === 'contracts') {
      fetchContracts();
    }
  }, [activeTab, filters, searchTerm]);

  const loadData = async () => {
    // Load all data at startup for badge counts
    await Promise.all([
      fetchPress(),
      fetchEvents(),
      fetchQuotes(),
      fetchOffers(),
      fetchContracts()
    ]);
  };

  const fetchPress = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.media_type && filters.media_type !== 'all') params.append('media_type', filters.media_type);
      if (filters.service_category && filters.service_category !== 'all') params.append('service_category', filters.service_category);
      if (filters.province) params.append('province', filters.province);
      if (filters.city) params.append('city', filters.city);
      if (filters.min_followers) params.append('min_followers', filters.min_followers);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      
      const response = await api.get(`/press/for-producers/list?${params.toString()}`);
      setPressList(response.data.items || []);
    } catch (error) {
      console.error('Error fetching press:', error);
      toast.error('Error al cargar medios de prensa');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await api.get('/press/quotes/producer');
      setMyQuotes(response.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await api.get('/press/offers/producer');
      setOffers(response.data || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get('/press/contracts/producer');
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/producers/me/events');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const openQuoteModal = (press) => {
    setSelectedPress(press);
    setQuoteForm({
      event_id: 'none',
      package_ids: [],
      message: '',
      budget_range: ''
    });
    setShowQuoteModal(true);
  };

  const sendQuoteRequest = async () => {
    // Message is optional if packages are selected
    if (quoteForm.package_ids.length === 0 && !quoteForm.message.trim()) {
      toast.error('Seleccioná al menos un paquete o escribí un mensaje');
      return;
    }

    setSendingQuote(true);
    try {
      // Don't auto-generate message for package-only quotes (enables simplified accept/reject flow)
      const messageToSend = quoteForm.message.trim() || '';

      await api.post('/press/quotes/request', {
        press_id: selectedPress.id,
        event_id: quoteForm.event_id && quoteForm.event_id !== 'none' ? quoteForm.event_id : null,
        package_ids: quoteForm.package_ids,
        message: messageToSend,
        budget_range: quoteForm.budget_range && quoteForm.budget_range !== 'none' ? quoteForm.budget_range : null
      });
      
      toast.success('Solicitud de cotización enviada');
      setShowQuoteModal(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSendingQuote(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await api.post(`/press/quotes/${quoteId}/accept`);
      toast.success('Cotización aceptada. Procedé al pago para activar el contrato.');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al aceptar cotización');
    }
  };

  const openCheckoutModal = (quote) => {
    setSelectedQuoteForPayment(quote);
    setShowCheckoutModal(true);
  };

  const initiatePayment = async (quoteId) => {
    setProcessingPayment(quoteId);
    try {
      const response = await api.post('/press/contracts/initiate-payment', {
        quote_id: quoteId
      });
      
      if (response.data.init_point) {
        // Redirect to MercadoPago
        window.location.href = response.data.init_point;
      } else {
        toast.error('Error al iniciar el pago. Contactá soporte.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessingPayment(null);
    }
  };

  const rejectQuote = async (quoteId) => {
    try {
      await api.post(`/press/quotes/${quoteId}/reject`);
      toast.success('Cotización rechazada');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al rechazar cotización');
    }
  };

  // Open offer checkout modal and fetch payment summary
  const openOfferCheckout = async (offer) => {
    setSelectedOfferForPayment(offer);
    setLoadingOfferSummary(true);
    setShowOfferCheckoutModal(true);
    
    try {
      const response = await api.get(`/press/offers/${offer.id}/payment-summary`);
      setOfferPaymentSummary(response.data);
    } catch (error) {
      console.error('Error fetching offer payment summary:', error);
      const errorMsg = error.response?.data?.detail || 'Error al cargar el resumen de pago';
      toast.error(errorMsg);
      setShowOfferCheckoutModal(false);
      setSelectedOfferForPayment(null);
    } finally {
      setLoadingOfferSummary(false);
    }
  };

  const respondToOffer = async (offerId, accept) => {
    if (accept) {
      // Find the offer and open checkout modal
      const offer = offers.find(o => o.id === offerId);
      if (offer) {
        openOfferCheckout(offer);
      }
      return;
    }
    
    // If rejecting, proceed directly
    try {
      await api.post(`/press/offers/${offerId}/respond?accept=false`);
      toast.success('Oferta rechazada');
      fetchOffers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al responder oferta';
      toast.error(errorMsg);
      console.error('Error responding to offer:', error.response?.data);
    }
  };

  const confirmOfferPayment = async () => {
    if (!selectedOfferForPayment) return;
    
    setProcessingOfferPayment(true);
    try {
      const response = await api.post(`/press/offers/${selectedOfferForPayment.id}/respond?accept=true`);
      
      if (response.data.init_point) {
        // Redirect to MercadoPago
        window.location.href = response.data.init_point;
      } else {
        toast.success('Oferta aceptada. El contrato está pendiente de pago.');
        setShowOfferCheckoutModal(false);
        fetchOffers();
        fetchContracts();
        setActiveTab('contracts');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la oferta');
    } finally {
      setProcessingOfferPayment(false);
    }
  };

  const updateContractStatus = async (contractId, status) => {
    try {
      await api.post(`/press/contracts/${contractId}/update-status`, { status });
      toast.success(`Contrato ${status === 'completed' ? 'completado' : status === 'in_progress' ? 'en progreso' : 'actualizado'}`);
      fetchContracts();
    } catch (error) {
      toast.error('Error al actualizar contrato');
    }
  };

  const [retryingPayment, setRetryingPayment] = useState(null);

  const retryContractPayment = async (contractId) => {
    setRetryingPayment(contractId);
    try {
      const response = await api.post(`/press/contracts/${contractId}/retry-payment`);
      
      if (response.data.init_point) {
        toast.success('Redirigiendo al pago...');
        window.location.href = response.data.init_point;
      } else {
        toast.error('Error al generar el link de pago');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reintentar el pago');
    } finally {
      setRetryingPayment(null);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getMinPrice = (press) => {
    const packages = press.service_packages || [];
    if (packages.length === 0) return null;
    const prices = packages.map(p => p.price).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const getTotalFollowers = (press) => {
    return (press.social_networks || []).reduce((sum, sn) => sum + (sn.followers || 0), 0);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      quoted: { label: 'Cotizado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      accepted: { label: 'Aceptado', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      rejected: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      paid: { label: 'Pagado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const getContractStatusBadge = (status) => {
    const config = {
      pending_payment: { label: 'Pendiente de Pago', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: CreditCard },
      active: { label: 'Activo', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      in_progress: { label: 'En Progreso', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Play },
      completed: { label: 'Completado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Check },
      cancelled: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X },
    };
    const c = config[status] || config.pending_payment;
    const Icon = c.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${c.color} flex items-center gap-1`}>
        <Icon size={12} />
        {c.label}
      </span>
    );
  };

  return (
    <ProducerLayout currentPage="press">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-3">
              <Newspaper className="text-violet-400" />
              Servicios
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Encontrá servicios para tus eventos
            </p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell 
              userType="producer" 
              apiEndpoint="/producer/notifications" 
              title="Notificaciones" 
            />
          </div>
        </div>

        {/* Tabs with Enhanced Badges */}
        <div className="flex gap-1 border-b border-zinc-800 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('browse')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'browse'
                ? 'bg-violet-500/20 text-violet-400 border-b-2 border-violet-500'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
            data-testid="tab-browse"
          >
            <Search size={18} />
            <span className="font-medium">Explorar Medios</span>
          </button>
          
          <button
            onClick={() => setActiveTab('quotes')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'quotes'
                ? 'bg-yellow-500/20 text-yellow-400 border-b-2 border-yellow-500'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
            data-testid="tab-quotes"
          >
            <MessageSquare size={18} />
            <span className="font-medium">Mis Cotizaciones</span>
            {myQuotes.filter(q => q.status === 'pending' || q.status === 'quoted').length > 0 ? (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-yellow-500 text-black animate-pulse">
                {myQuotes.filter(q => q.status === 'pending' || q.status === 'quoted').length}
              </span>
            ) : (
              <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
                activeTab === 'quotes' ? 'bg-yellow-500/30 text-yellow-400' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {myQuotes.length || 0}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('offers')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'offers'
                ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
            data-testid="tab-offers"
          >
            <Sparkles size={18} />
            <span className="font-medium">Ofertas Recibidas</span>
            {offers.filter(o => o.status === 'pending').length > 0 ? (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-purple-500 text-white animate-pulse">
                {offers.filter(o => o.status === 'pending').length}
              </span>
            ) : (
              <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
                activeTab === 'offers' ? 'bg-purple-500/30 text-purple-400' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {offers.length || 0}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('contracts')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'contracts'
                ? 'bg-green-500/20 text-green-400 border-b-2 border-green-500'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
            data-testid="tab-contracts"
          >
            <FileText size={18} />
            <span className="font-medium">Contratos</span>
            {contracts.filter(c => c.status === 'active' || c.status === 'in_progress').length > 0 ? (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-green-500 text-black">
                {contracts.filter(c => c.status === 'active' || c.status === 'in_progress').length}
              </span>
            ) : (
              <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
                activeTab === 'contracts' ? 'bg-green-500/30 text-green-400' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {contracts.length || 0}
              </span>
            )}
          </button>
        </div>

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <>
            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="pl-10 bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* Service Category Filter - Main filter */}
                <Select value={filters.service_category} onValueChange={(v) => setFilters({ ...filters, service_category: v })}>
                  <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Categoría de servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.media_type} onValueChange={(v) => setFilters({ ...filters, media_type: v })}>
                  <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Plataforma" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.sort_by} onValueChange={(v) => setFilters({ ...filters, sort_by: v })}>
                  <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-violet-500/20 border-violet-500/50' : ''}
                >
                  <Filter size={18} />
                </Button>
              </div>
            </div>

            {/* Extended Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                >
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Mín. Seguidores</label>
                      <Input
                        type="number"
                        value={filters.min_followers}
                        onChange={(e) => setFilters({ ...filters, min_followers: e.target.value })}
                        placeholder="1000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Precio máx.</label>
                      <Input
                        type="number"
                        value={filters.max_price}
                        onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                        placeholder="50000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Provincia</label>
                      <Input
                        value={filters.province}
                        onChange={(e) => setFilters({ ...filters, province: e.target.value })}
                        placeholder="Buenos Aires"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Ciudad</label>
                      <Input
                        value={filters.city}
                        onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                        placeholder="Capital Federal"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        variant="ghost" 
                        onClick={() => setFilters({ media_type: 'all', service_category: 'all', province: '', city: '', min_followers: '', max_price: '', sort_by: 'monthly_reach' })}
                        className="w-full text-zinc-400"
                      >
                        Limpiar filtros
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Press Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-violet-400" size={32} />
              </div>
            ) : pressList.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                <Newspaper className="mx-auto mb-4 text-zinc-600" size={48} />
                <p>No se encontraron medios de prensa</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pressList.map((press) => (
                  <motion.div
                    key={press.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all group"
                  >
                    {/* Header with cover or gradient */}
                    <div className="h-24 bg-gradient-to-br from-violet-500/20 to-pink-500/20 relative">
                      {press.cover_image_url && (
                        <img src={press.cover_image_url} alt="" className="w-full h-full object-cover" />
                      )}
                      {(press.is_featured || press.is_verified) && (
                        <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                          <Star size={12} />
                          Destacado
                        </div>
                      )}
                    </div>

                    {/* Profile */}
                    <div className="p-4 -mt-10 relative">
                      <div className="w-16 h-16 rounded-xl bg-zinc-800 border-2 border-zinc-900 flex items-center justify-center overflow-hidden mb-3">
                        {press.logo_url ? (
                          <img src={press.logo_url} alt={press.media_name} className="w-full h-full object-cover" />
                        ) : (
                          <Newspaper className="text-violet-400" size={28} />
                        )}
                      </div>

                      <h3 className="text-white font-bold text-lg">{press.media_name}</h3>
                      <p className="text-zinc-500 text-sm capitalize">{press.media_type}</p>

                      {/* Service Categories - NEW */}
                      {press.service_categories && press.service_categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {press.service_categories.slice(0, 3).map((cat) => {
                            const category = SERVICE_CATEGORIES.find(c => c.value === cat);
                            return (
                              <span 
                                key={cat} 
                                className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full border border-violet-500/30"
                              >
                                {category?.label?.split('/')[0]?.split(' ')[0] || cat}
                              </span>
                            );
                          })}
                          {press.service_categories.length > 3 && (
                            <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded-full">
                              +{press.service_categories.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Portfolio Preview */}
                      {press.portfolio && press.portfolio.length > 0 && (
                        <div className="mt-3">
                          <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                            <ImageIcon size={12} />
                            Portfolio ({press.portfolio.length})
                          </p>
                          <div className="flex gap-1.5 overflow-hidden">
                            {press.portfolio.slice(0, 3).map((item, idx) => (
                              <button 
                                key={item.id || idx} 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPortfolioItem(item);
                                  setSelectedPressForPortfolio(press);
                                  setShowPortfolioModal(true);
                                }}
                                className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700 hover:border-yellow-500/50 transition-colors cursor-pointer"
                              >
                                <ImageWithFallback 
                                  src={item.image_url} 
                                  alt={item.title} 
                                  className="w-full h-full object-cover" 
                                  fallbackIcon={Camera}
                                />
                              </button>
                            ))}
                            {press.portfolio.length > 3 && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPortfolioItem(press.portfolio[3]);
                                  setSelectedPressForPortfolio(press);
                                  setShowPortfolioModal(true);
                                }}
                                className="w-12 h-12 rounded-lg bg-zinc-800/80 flex-shrink-0 border border-zinc-700 hover:border-yellow-500/50 transition-colors flex items-center justify-center cursor-pointer"
                              >
                                <span className="text-zinc-400 text-xs font-medium">+{press.portfolio.length - 3}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 mt-4">
                        <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-white font-bold">{formatNumber(getTotalFollowers(press))}</p>
                          <p className="text-zinc-500 text-xs">Seguidores</p>
                        </div>
                        <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-white font-bold">{formatNumber(press.statistics?.monthly_reach || 0)}</p>
                          <p className="text-zinc-500 text-xs">Alcance Mensual</p>
                        </div>
                        <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-white font-bold">{formatNumber(press.statistics?.average_story_views || 0)}</p>
                          <p className="text-zinc-500 text-xs">Vistas Stories</p>
                        </div>
                      </div>

                      {/* Price & Location */}
                      <div className="flex items-center justify-between mt-4 text-sm">
                        {getMinPrice(press) && (
                          <div className="flex items-center gap-1 text-green-400">
                            <DollarSign size={14} />
                            <span>Desde ${getMinPrice(press).toLocaleString()}</span>
                          </div>
                        )}
                        {press.province && (
                          <div className="flex items-center gap-1 text-zinc-500">
                            <MapPin size={14} />
                            <span>{press.province}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setSelectedPress(press);
                            setShowDetailModal(true);
                          }}
                        >
                          <Eye size={14} className="mr-1" />
                          Ver Perfil
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500"
                          onClick={() => openQuoteModal(press)}
                        >
                          <Send size={14} className="mr-1" />
                          Cotizar
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="space-y-4">
            {myQuotes.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                <MessageSquare className="mx-auto mb-4 text-zinc-600" size={48} />
                <p>No tenés cotizaciones solicitadas</p>
                <p className="text-sm mt-2">Explorá medios de prensa y solicitá cotizaciones</p>
              </div>
            ) : (
              myQuotes.map((quote) => (
                <motion.div
                  key={quote.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        {quote.press?.logo_url ? (
                          <img src={quote.press.logo_url} alt="" className="w-full h-full rounded-xl object-cover" />
                        ) : (
                          <Newspaper className="text-violet-400" size={24} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-bold">{quote.press_name}</h3>
                          {getStatusBadge(quote.status)}
                        </div>
                        {quote.event_name && (
                          <p className="text-zinc-500 text-sm">Para: {quote.event_name}</p>
                        )}
                        <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{quote.message}</p>
                        <p className="text-zinc-600 text-xs mt-1">
                          {new Date(quote.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Quote Response */}
                    {quote.status === 'quoted' && quote.response && (
                      <div className="bg-zinc-800/50 rounded-xl p-4 lg:w-80">
                        <p className="text-white font-bold text-xl">
                          ${quote.response.quote_amount?.toLocaleString()}
                          <span className="text-zinc-500 text-sm font-normal ml-1">{quote.response.currency}</span>
                        </p>
                        <p className="text-zinc-400 text-sm mt-1">{quote.response.message}</p>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            onClick={() => acceptQuote(quote.id)}
                          >
                            <Check size={14} className="mr-1" />
                            Aceptar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="flex-1 text-red-400 hover:bg-red-500/10"
                            onClick={() => rejectQuote(quote.id)}
                          >
                            <X size={14} className="mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Payment Button for Accepted Quotes */}
                    {quote.status === 'accepted' && quote.response && (
                      <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4 lg:w-80">
                        <p className="text-green-400 text-sm font-medium mb-2">Cotización aceptada</p>
                        <p className="text-white font-bold text-xl">
                          ${quote.response.quote_amount?.toLocaleString()}
                          <span className="text-zinc-500 text-sm font-normal ml-1">{quote.response.currency}</span>
                        </p>
                        <Button 
                          size="sm" 
                          className="w-full mt-3 bg-green-500 hover:bg-green-600 text-white"
                          onClick={() => openCheckoutModal(quote)}
                          data-testid={`pay-quote-${quote.id}`}
                        >
                          <CreditCard size={14} className="mr-1" />
                          Ver Resumen y Pagar
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            {offers.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                <Sparkles className="mx-auto mb-4 text-zinc-600" size={48} />
                <p>No tenés ofertas de prensa</p>
                <p className="text-sm mt-2">Los medios pueden ofrecerte servicios proactivamente</p>
              </div>
            ) : (
              offers.map((offer) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                  data-testid={`offer-${offer.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        {offer.press?.logo_url ? (
                          <img src={offer.press.logo_url} alt="" className="w-full h-full rounded-xl object-cover" />
                        ) : (
                          <Newspaper className="text-purple-400" size={24} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-bold">{offer.press_name}</h3>
                          {getStatusBadge(offer.status)}
                        </div>
                        {offer.event_name && (
                          <p className="text-zinc-500 text-sm">Para evento: {offer.event_name}</p>
                        )}
                        
                        {/* Message from press */}
                        {offer.message && (
                          <p className="text-zinc-400 text-sm mt-2 p-3 bg-zinc-800/50 rounded-lg border-l-2 border-purple-500/50">
                            "{offer.message}"
                          </p>
                        )}
                        
                        {/* Packages offered */}
                        {offer.package_ids?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-zinc-500 text-xs mb-2">Paquetes ofrecidos:</p>
                            <div className="flex flex-wrap gap-2">
                              {offer.package_ids.map((pkgId, idx) => {
                                const pkg = offer.press?.service_packages?.find(p => p.id === pkgId || p.name === pkgId);
                                return (
                                  <div key={idx} className="bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2">
                                    <p className="text-purple-400 font-medium text-sm">{pkg?.name || pkgId}</p>
                                    {pkg?.price && (
                                      <p className="text-emerald-400 text-xs font-bold">${pkg.price.toLocaleString()}</p>
                                    )}
                                    {pkg?.includes?.length > 0 && (
                                      <div className="mt-1 space-y-0.5">
                                        {pkg.includes.slice(0, 2).map((item, i) => (
                                          <p key={i} className="text-zinc-400 text-xs flex items-center gap-1">
                                            <Check size={10} className="text-green-400" />
                                            {item}
                                          </p>
                                        ))}
                                        {pkg.includes.length > 2 && (
                                          <p className="text-zinc-500 text-xs">+{pkg.includes.length - 2} más</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-zinc-600 text-xs mt-3">
                          Recibida: {new Date(offer.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="lg:text-right">
                      {/* Price Display */}
                      {(offer.proposed_price || offer.package_ids?.length > 0) && (
                        <div className="mb-3">
                          <p className="text-zinc-500 text-xs">Precio propuesto</p>
                          <p className="text-emerald-400 font-bold text-xl">
                            ${(offer.proposed_price || 
                               offer.press?.service_packages
                                 ?.filter(p => offer.package_ids?.includes(p.id || p.name))
                                 .reduce((sum, p) => sum + (p.price || 0), 0) || 0
                            ).toLocaleString()} ARS
                          </p>
                          {/* Platform fee notice */}
                          {offer.status === 'pending' && (
                            <p className="text-yellow-400/70 text-xs mt-1">
                              + Costo de servicio de plataforma
                            </p>
                          )}
                        </div>
                      )}
                      
                      {offer.status === 'pending' && (
                        <div className="flex gap-2 lg:justify-end">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-500 text-white"
                            onClick={() => respondToOffer(offer.id, true)}
                            data-testid={`accept-offer-${offer.id}`}
                          >
                            <Check size={14} className="mr-1" />
                            Ver Resumen y Aceptar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={() => respondToOffer(offer.id, false)}
                            data-testid={`reject-offer-${offer.id}`}
                          >
                            <X size={14} className="mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                      
                      {offer.status === 'accepted' && (
                        <div className="text-emerald-400 text-sm flex items-center gap-2 lg:justify-end">
                          <CheckCircle size={16} />
                          Aceptada
                        </div>
                      )}
                      
                      {offer.status === 'rejected' && (
                        <div className="text-red-400 text-sm flex items-center gap-2 lg:justify-end">
                          <XCircle size={16} />
                          Rechazada
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <div className="space-y-4" data-testid="contracts-tab">
            {contracts.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                <FileText className="mx-auto mb-4 text-zinc-600" size={48} />
                <p>No tenés contratos activos</p>
                <p className="text-sm mt-2">Los contratos se crean al aceptar y pagar cotizaciones</p>
              </div>
            ) : (
              contracts.map((contract) => (
                <motion.div
                  key={contract.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                  data-testid={`contract-${contract.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        {contract.press?.logo_url ? (
                          <img src={contract.press.logo_url} alt="" className="w-full h-full rounded-xl object-cover" />
                        ) : (
                          <FileText className="text-emerald-400" size={24} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-bold">{contract.press_name}</h3>
                          {getContractStatusBadge(contract.status)}
                        </div>
                        {contract.event_name && (
                          <p className="text-zinc-500 text-sm">Evento: {contract.event_name}</p>
                        )}
                        <p className="text-zinc-600 text-xs mt-1">
                          Contrato #{contract.id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-zinc-600 text-xs">
                          Creado: {new Date(contract.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="lg:text-right">
                      <p className="text-white font-bold text-xl">
                        ${contract.total_amount?.toLocaleString()}
                      </p>
                      <p className="text-zinc-500 text-xs">
                        Costo servicio plataforma: ${contract.platform_fee?.toLocaleString()}
                        {contract.fee_paid_by === 'press' || contract.press_absorbs_fee ? (
                          <span className="text-yellow-400"> (absorbida por el medio)</span>
                        ) : (
                          <span className="text-blue-400"> (pagada por productora)</span>
                        )}
                      </p>
                      <p className="text-emerald-400 text-sm mt-1">
                        Recibe el medio: ${contract.press_amount?.toLocaleString()}
                      </p>
                      
                      {/* Pending Payment Actions */}
                      {contract.status === 'pending_payment' && (
                        <div className="flex gap-2 mt-3 justify-end flex-wrap">
                          <Button 
                            size="sm" 
                            className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                            onClick={() => retryContractPayment(contract.id)}
                            disabled={retryingPayment === contract.id}
                            data-testid={`retry-payment-${contract.id}`}
                          >
                            {retryingPayment === contract.id ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <CreditCard size={14} className="mr-1" />
                            )}
                            {retryingPayment === contract.id ? 'Procesando...' : 'Reintentar Pago'}
                          </Button>
                        </div>
                      )}
                      
                      {/* Contract Actions */}
                      {(contract.status === 'active' || contract.status === 'in_progress') && (
                        <div className="flex gap-2 mt-3 justify-end flex-wrap">
                          {/* Chat only available after work is started */}
                          {contract.status === 'in_progress' && (
                            <Button 
                              size="sm" 
                              className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                              onClick={() => {
                                setSelectedContractForChat(contract);
                                setShowChatModal(true);
                              }}
                            >
                              <MessageSquare size={14} className="mr-1" />
                              Chat
                            </Button>
                          )}
                          
                          {contract.status === 'active' && (
                            <Button 
                              size="sm" 
                              className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                              onClick={() => updateContractStatus(contract.id, 'in_progress')}
                            >
                              <Play size={14} className="mr-1" />
                              Iniciar Trabajo
                            </Button>
                          )}
                          
                          {contract.status === 'in_progress' && (
                            <Button 
                              size="sm" 
                              className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                              onClick={() => updateContractStatus(contract.id, 'completed')}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Marcar Completado
                            </Button>
                          )}
                        </div>
                      )}

                      {contract.status === 'completed' && (
                        <div className="mt-3 space-y-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full lg:w-auto"
                            onClick={() => {
                              setSelectedContractForChat(contract);
                              setShowChatModal(true);
                            }}
                          >
                            <MessageSquare size={14} className="mr-1" />
                            Ver Historial de Chat
                          </Button>
                          {contract.completed_at && (
                            <p className="text-emerald-400 text-xs">
                              Completado: {new Date(contract.completed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Service Details Panel */}
                  {(contract.package_ids?.length > 0 || contract.quote_message || contract.response_message) && (
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <p className="text-zinc-500 text-xs font-medium uppercase tracking-wider mb-3 flex items-center gap-1">
                        <Package size={12} />
                        Servicio Contratado
                      </p>
                      
                      {/* Packages */}
                      {contract.package_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {contract.package_ids.map((pkgId, idx) => {
                            const pkg = contract.press?.service_packages?.find(p => p.id === pkgId || p.name === pkgId);
                            return (
                              <div key={idx} className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2">
                                <p className="text-blue-400 font-medium text-sm">{pkg?.name || pkgId}</p>
                                {pkg?.price && <p className="text-emerald-400 text-xs">${pkg.price.toLocaleString()}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Quote message */}
                      {contract.quote_message && (
                        <div className="bg-zinc-800/30 rounded-lg p-3 mb-2">
                          <p className="text-zinc-500 text-xs mb-1">Tu mensaje:</p>
                          <p className="text-zinc-300 text-sm">{contract.quote_message}</p>
                        </div>
                      )}
                      
                      {/* Response message */}
                      {contract.response_message && (
                        <div className="bg-zinc-800/30 rounded-lg p-3">
                          <p className="text-zinc-500 text-xs mb-1">Respuesta del medio:</p>
                          <p className="text-zinc-300 text-sm">{contract.response_message}</p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Press Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedPress && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-violet-500/20 rounded-xl flex items-center justify-center overflow-hidden">
                    {selectedPress.logo_url ? (
                      <img src={selectedPress.logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Newspaper className="text-violet-400" size={32} />
                    )}
                  </div>
                  <div>
                    <DialogTitle className="text-white text-xl">{selectedPress.media_name}</DialogTitle>
                    <p className="text-zinc-500 capitalize">{selectedPress.media_type}</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Description */}
                {selectedPress.description && (
                  <div>
                    <h4 className="text-zinc-400 text-sm font-medium mb-2">Descripción</h4>
                    <p className="text-white">{selectedPress.description}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-zinc-800/50 rounded-xl">
                    <Users className="mx-auto text-violet-400 mb-1" size={20} />
                    <p className="text-white font-bold">{formatNumber(getTotalFollowers(selectedPress))}</p>
                    <p className="text-zinc-500 text-xs">Seguidores</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800/50 rounded-xl">
                    <TrendingUp className="mx-auto text-green-400 mb-1" size={20} />
                    <p className="text-white font-bold">{formatNumber(selectedPress.statistics?.monthly_reach || 0)}</p>
                    <p className="text-zinc-500 text-xs">Alcance Mensual</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800/50 rounded-xl">
                    <Play className="mx-auto text-pink-400 mb-1" size={20} />
                    <p className="text-white font-bold">{formatNumber(selectedPress.statistics?.average_story_views || 0)}</p>
                    <p className="text-zinc-500 text-xs">Vistas Stories</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800/50 rounded-xl">
                    <CheckCircle className="mx-auto text-purple-400 mb-1" size={20} />
                    <p className="text-white font-bold">{selectedPress.completed_works_count || 0}</p>
                    <p className="text-zinc-500 text-xs">Trabajos Realizados</p>
                  </div>
                </div>

                {/* Social Networks - Clickable Links */}
                {selectedPress.social_networks?.length > 0 && (
                  <div>
                    <h4 className="text-zinc-400 text-sm font-medium mb-2">Redes Sociales</h4>
                    <div className="space-y-2">
                      {selectedPress.social_networks.map((sn, i) => (
                        <a 
                          key={i} 
                          href={sn.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-between bg-zinc-800/50 hover:bg-zinc-800 rounded-lg px-4 py-3 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            {sn.platform === 'instagram' && <Instagram size={20} className="text-pink-400" />}
                            {sn.platform === 'tiktok' && <span className="text-cyan-400 font-bold">TT</span>}
                            {sn.platform === 'youtube' && <span className="text-red-400 font-bold">YT</span>}
                            {sn.platform === 'twitter' && <span className="text-blue-400 font-bold">X</span>}
                            {sn.platform === 'facebook' && <span className="text-blue-500 font-bold">FB</span>}
                            {sn.platform === 'website' && <Globe size={20} className="text-green-400" />}
                            {!['instagram', 'tiktok', 'youtube', 'twitter', 'facebook', 'website'].includes(sn.platform) && 
                              <Globe size={20} className="text-zinc-400" />
                            }
                            <div>
                              <p className="text-white capitalize font-medium">{sn.platform}</p>
                              {sn.username && <p className="text-zinc-500 text-sm">@{sn.username}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-blue-400 font-bold">{formatNumber(sn.followers)}</span>
                            <ExternalLink size={14} className="text-zinc-500 group-hover:text-blue-400 transition-colors" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audience - Enhanced */}
                {selectedPress.audience && (
                  <div>
                    <h4 className="text-zinc-400 text-sm font-medium mb-2">Audiencia / Target</h4>
                    <div className="bg-zinc-800/50 rounded-xl p-4 space-y-4">
                      {/* Basic Demographics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <p className="text-white font-bold text-lg">{selectedPress.audience.age_range_min}-{selectedPress.audience.age_range_max}</p>
                          <p className="text-zinc-500 text-xs">Rango de Edad</p>
                        </div>
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <p className="text-white font-bold text-lg capitalize">{selectedPress.audience.gender_distribution || 'Mixto'}</p>
                          <p className="text-zinc-500 text-xs">Género</p>
                        </div>
                        {selectedPress.audience.education_level && (
                          <div className="bg-zinc-900/50 rounded-lg p-3">
                            <p className="text-white font-bold text-lg capitalize">{selectedPress.audience.education_level}</p>
                            <p className="text-zinc-500 text-xs">Educación</p>
                          </div>
                        )}
                        {selectedPress.audience.income_level && (
                          <div className="bg-zinc-900/50 rounded-lg p-3">
                            <p className="text-white font-bold text-lg capitalize">{selectedPress.audience.income_level}</p>
                            <p className="text-zinc-500 text-xs">Nivel Económico</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Peak Activity */}
                      {selectedPress.audience.peak_activity_hours && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock size={14} className="text-yellow-400" />
                          <span className="text-zinc-400">Mayor actividad:</span>
                          <span className="text-yellow-400 font-medium">{selectedPress.audience.peak_activity_hours}</span>
                        </div>
                      )}

                      {/* Locations */}
                      {selectedPress.audience.locations?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                            <MapPin size={12} />
                            Ubicaciones principales
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {selectedPress.audience.locations.map((loc, i) => (
                              <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                {loc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Interests */}
                      {selectedPress.audience.interests?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs mb-2">Intereses musicales</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedPress.audience.interests.map((int, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                {int}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Lifestyle */}
                      {selectedPress.audience.lifestyle?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs mb-2">Estilo de vida</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedPress.audience.lifestyle.map((ls, i) => (
                              <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                                {ls}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Occupation Types */}
                      {selectedPress.audience.occupation_types?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs mb-2">Ocupaciones</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedPress.audience.occupation_types.map((occ, i) => (
                              <span key={i} className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                                {occ}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Content Preferences */}
                      {selectedPress.audience.content_preferences?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs mb-2">Preferencias de contenido</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedPress.audience.content_preferences.map((cp, i) => (
                              <span key={i} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                                {cp}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Demographics */}
                      {selectedPress.audience.custom_demographics?.length > 0 && (
                        <div className="border-t border-zinc-700 pt-3 mt-3">
                          <p className="text-zinc-500 text-xs mb-2">Datos adicionales</p>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedPress.audience.custom_demographics.map((cd, i) => (
                              <div key={i} className="bg-zinc-900/50 rounded-lg p-2 text-center">
                                <p className="text-white font-medium text-sm">{cd.value}</p>
                                <p className="text-zinc-500 text-xs">{cd.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Portfolio Section in Detail Modal */}
                {selectedPress.portfolio && selectedPress.portfolio.length > 0 && (
                  <div>
                    <h4 className="text-zinc-400 text-sm font-medium mb-3 flex items-center gap-2">
                      <ImageIcon size={16} />
                      Portfolio ({selectedPress.portfolio.length} trabajos)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedPress.portfolio.map((item, idx) => (
                        <button
                          key={item.id || idx}
                          onClick={() => {
                            setSelectedPortfolioItem(item);
                            setSelectedPressForPortfolio(selectedPress);
                            setShowPortfolioModal(true);
                          }}
                          className="group relative bg-zinc-800 rounded-xl overflow-hidden aspect-video hover:ring-2 hover:ring-yellow-500/50 transition-all"
                        >
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                              <ImageIcon className="text-zinc-600" size={32} />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                            <div>
                              <p className="text-white font-medium text-sm truncate">{item.title}</p>
                              {item.event_name && (
                                <p className="text-zinc-400 text-xs truncate">{item.event_name}</p>
                              )}
                            </div>
                          </div>
                          {/* View indicator */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-yellow-500 rounded-full p-1.5">
                              <Eye className="text-black" size={14} />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Service Packages */}
                {selectedPress.service_packages?.length > 0 && (
                  <div>
                    <h4 className="text-zinc-400 text-sm font-medium mb-2">Paquetes de Servicios</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {selectedPress.service_packages.map((pkg, i) => (
                        <div key={i} className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="text-white font-bold">{pkg.name}</h5>
                              {pkg.description && (
                                <p className="text-zinc-500 text-sm">{pkg.description}</p>
                              )}
                            </div>
                            <p className="text-green-400 font-bold">
                              ${pkg.price?.toLocaleString()}
                            </p>
                          </div>
                          {pkg.includes?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {pkg.includes.map((inc, j) => (
                                <li key={j} className="text-zinc-400 text-sm flex items-center gap-1">
                                  <Check size={12} className="text-green-400" />
                                  {inc}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Cerrar
                </Button>
                <Button 
                  className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500"
                  onClick={() => {
                    setShowDetailModal(false);
                    openQuoteModal(selectedPress);
                  }}
                >
                  <Send size={16} className="mr-2" />
                  Solicitar Cotización
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Request Modal - Professional Redesign */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[90vh] p-0 overflow-hidden" data-testid="quote-request-modal">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-bold">Solicitar Cotización</DialogTitle>
            </DialogHeader>
            
            {selectedPress && (
              <div className="flex items-center gap-4 mt-4 bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
                <div className="w-14 h-14 bg-violet-500/20 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selectedPress.logo_url ? (
                    <img src={selectedPress.logo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="text-violet-400" size={28} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold text-lg">{selectedPress.media_name}</p>
                  <div className="flex items-center gap-4 text-sm text-zinc-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {formatNumber(getTotalFollowers(selectedPress))} seguidores
                    </span>
                    {selectedPress.statistics?.monthly_reach > 0 && (
                      <span className="flex items-center gap-1">
                        <TrendingUp size={14} className="text-green-400" />
                        {formatNumber(selectedPress.statistics.monthly_reach)} alcance
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {selectedPress && (
            <div className="overflow-y-auto px-6 py-6 space-y-6 max-h-[calc(90vh-220px)]">
              {/* Event & Budget Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Evento (opcional)</label>
                  <Select value={quoteForm.event_id} onValueChange={(v) => setQuoteForm({ ...quoteForm, event_id: v })}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 focus:border-blue-500">
                      <SelectValue placeholder="Seleccionar evento" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">Sin evento específico</SelectItem>
                      {events.filter(e => (e.title || e.name) && e.status !== 'draft' && e.status !== 'cancelled').map(e => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.title || e.name} {e.date ? `(${new Date(e.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {events.filter(e => (e.title || e.name) && e.status !== 'draft' && e.status !== 'cancelled').length === 0 && (
                    <p className="text-zinc-500 text-xs mt-1">No tenés eventos publicados. Podés crear uno desde el panel de eventos.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Presupuesto estimado (opcional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number"
                      value={quoteForm.budget_range}
                      onChange={(e) => setQuoteForm({ ...quoteForm, budget_range: e.target.value })}
                      placeholder="Ej: 25000"
                      className="bg-zinc-900 border-zinc-800 focus:border-blue-500 pl-8"
                    />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Indicá tu presupuesto aproximado para este servicio</p>
                </div>
              </div>

              {/* Service Packages Grid - Professional Cards */}
              {selectedPress.service_packages?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                      <Package size={16} className="text-blue-400" />
                      Paquetes de Servicios Disponibles
                    </label>
                    {quoteForm.package_ids.length > 0 && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full font-medium">
                        {quoteForm.package_ids.length} seleccionado{quoteForm.package_ids.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedPress.service_packages.map((pkg) => {
                      const pkgId = pkg.id || pkg.name;
                      const isSelected = quoteForm.package_ids.includes(pkgId);
                      
                      return (
                        <div
                          key={pkgId}
                          onClick={() => {
                            setQuoteForm({
                              ...quoteForm,
                              package_ids: isSelected
                                ? quoteForm.package_ids.filter(p => p !== pkgId)
                                : [...quoteForm.package_ids, pkgId]
                            });
                          }}
                          className={`relative flex flex-col p-5 rounded-xl border-2 transition-all cursor-pointer group ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-500/5 ring-1 ring-blue-500/20' 
                              : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-900/80'
                          }`}
                          data-testid={`package-card-${pkgId}`}
                        >
                          {/* Selection indicator */}
                          <div className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected 
                              ? 'bg-blue-500 border-blue-500' 
                              : 'border-zinc-600 group-hover:border-zinc-500'
                          }`}>
                            {isSelected && <Check size={14} className="text-white" />}
                          </div>
                          
                          {/* Header */}
                          <div className="flex justify-between items-start mb-3 pr-8">
                            <div>
                              <h4 className="text-lg font-bold text-white">{pkg.name}</h4>
                              {pkg.category && (
                                <span className="text-xs text-zinc-500 capitalize">{pkg.category}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Price */}
                          <div className="flex items-baseline gap-1 mb-3">
                            <span className="text-2xl font-bold text-emerald-400">${pkg.price?.toLocaleString()}</span>
                            <span className="text-sm text-zinc-500">{pkg.currency || 'ARS'}</span>
                          </div>
                          
                          {/* Description */}
                          {pkg.description && (
                            <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{pkg.description}</p>
                          )}
                          
                          {/* Meta Row - Delivery & Platforms */}
                          <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4 pb-4 border-b border-zinc-800">
                            {pkg.delivery_time && (
                              <span className="flex items-center gap-1.5">
                                <Clock size={14} className="text-yellow-400" />
                                {pkg.delivery_time}
                              </span>
                            )}
                            {pkg.platforms?.length > 0 && (
                              <span className="flex items-center gap-1.5">
                                <Globe size={14} className="text-purple-400" />
                                {pkg.platforms.slice(0, 3).join(', ')}
                                {pkg.platforms.length > 3 && ` +${pkg.platforms.length - 3}`}
                              </span>
                            )}
                            {pkg.revisions && (
                              <span className="flex items-center gap-1.5">
                                <Eye size={14} className="text-cyan-400" />
                                {pkg.revisions === 'unlimited' ? 'Revisiones ilim.' : `${pkg.revisions} rev.`}
                              </span>
                            )}
                          </div>
                          
                          {/* Features List - What's Included */}
                          {pkg.includes?.length > 0 && (
                            <div className="space-y-2 flex-grow">
                              <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider mb-2">Qué incluye:</p>
                              {pkg.includes.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                                  <Check size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                  <span>{item}</span>
                                </div>
                              ))}
                              {pkg.includes.length > 5 && (
                                <p className="text-xs text-zinc-500 ml-6">+{pkg.includes.length - 5} más...</p>
                              )}
                            </div>
                          )}
                          
                          {/* Empty state for packages without includes */}
                          {(!pkg.includes || pkg.includes.length === 0) && (
                            <div className="flex-grow flex items-center justify-center py-4">
                              <p className="text-sm text-zinc-600">Contactar para más detalles</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Message Section */}
              <div className="pt-4 border-t border-zinc-800">
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  <MessageSquare size={14} className="inline mr-1.5 text-blue-400" />
                  Mensaje adicional
                  {quoteForm.package_ids.length === 0 && <span className="text-red-400 ml-1">*</span>}
                  {quoteForm.package_ids.length > 0 && (
                    <span className="text-zinc-500 text-xs ml-2">(opcional si seleccionaste paquetes)</span>
                  )}
                </label>
                <Textarea
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                  placeholder="Contale al medio qué necesitás: fechas específicas, detalles del evento, requerimientos especiales..."
                  className="bg-zinc-900 border-zinc-800 focus:border-blue-500 min-h-[120px] resize-none"
                />
              </div>
              
              {/* Summary */}
              {quoteForm.package_ids.length > 0 && (
                <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Total estimado de paquetes:</span>
                    <span className="text-emerald-400 font-bold text-xl">
                      ${selectedPress.service_packages
                        .filter(p => quoteForm.package_ids.includes(p.id || p.name))
                        .reduce((sum, p) => sum + (p.price || 0), 0)
                        .toLocaleString()} ARS
                    </span>
                  </div>
                  <p className="text-zinc-600 text-xs mt-1">* El precio final será confirmado por el medio</p>
                </div>
              )}
            </div>
          )}

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800 px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => setShowQuoteModal(false)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button 
              onClick={sendQuoteRequest}
              disabled={sendingQuote || (quoteForm.package_ids.length === 0 && !quoteForm.message.trim())}
              className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-6"
              data-testid="send-quote-btn"
            >
              {sendingQuote ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Enviar Solicitud
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={showCheckoutModal} onOpenChange={setShowCheckoutModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Resumen de Pago</DialogTitle>
          </DialogHeader>
          
          {selectedQuoteForPayment && selectedQuoteForPayment.response && (() => {
            const amounts = calculateCheckoutAmounts(selectedQuoteForPayment);
            return (
            <div className="space-y-5 py-4">
              {/* Press Info */}
              <div className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-4">
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selectedQuoteForPayment.press?.logo_url ? (
                    <img src={selectedQuoteForPayment.press.logo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="text-violet-400" size={24} />
                  )}
                </div>
                <div>
                  <p className="text-white font-bold">{selectedQuoteForPayment.press_name}</p>
                  {selectedQuoteForPayment.event_name && (
                    <p className="text-zinc-500 text-sm">Para: {selectedQuoteForPayment.event_name}</p>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-zinc-800/30 rounded-xl p-5 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Servicio cotizado</span>
                  <span className="text-white font-medium">
                    ${amounts.serviceCost.toLocaleString()} {selectedQuoteForPayment.response.currency}
                  </span>
                </div>
                
                {amounts.showFee && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Costo de servicio</span>
                    <span className="text-yellow-400 font-medium">
                      ${amounts.platformFee.toLocaleString()} {selectedQuoteForPayment.response.currency}
                    </span>
                  </div>
                )}

                <div className="border-t border-zinc-700 pt-4 flex justify-between items-center">
                  <span className="text-white font-bold text-lg">Total a pagar</span>
                  <span className="text-green-400 font-black text-2xl">
                    ${amounts.total.toLocaleString()}
                    <span className="text-zinc-500 text-sm font-normal ml-1">{selectedQuoteForPayment.response.currency}</span>
                  </span>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium">Pago seguro con MercadoPago</p>
                  <p className="text-zinc-400 mt-1">
                    El contrato se activa automáticamente una vez confirmado el pago. El medio será notificado para comenzar el trabajo.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowCheckoutModal(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-[#009ee3] hover:bg-[#008ac9] text-white"
                  onClick={() => {
                    setShowCheckoutModal(false);
                    initiatePayment(selectedQuoteForPayment.id);
                  }}
                  disabled={processingPayment === selectedQuoteForPayment.id}
                >
                  {processingPayment === selectedQuoteForPayment.id ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <CreditCard size={16} className="mr-2" />
                  )}
                  Pagar con MercadoPago
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Offer Checkout Modal */}
      <Dialog open={showOfferCheckoutModal} onOpenChange={setShowOfferCheckoutModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Confirmar Oferta</DialogTitle>
          </DialogHeader>
          
          {selectedOfferForPayment && (
            <div className="space-y-5 py-4">
              {/* Press Info */}
              <div className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selectedOfferForPayment.press?.logo_url ? (
                    <img src={selectedOfferForPayment.press.logo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="text-purple-400" size={24} />
                  )}
                </div>
                <div>
                  <p className="text-white font-bold">{selectedOfferForPayment.press_name}</p>
                  {selectedOfferForPayment.event_name && (
                    <p className="text-zinc-500 text-sm">Para evento: {selectedOfferForPayment.event_name}</p>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              {loadingOfferSummary ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-purple-400" size={32} />
                </div>
              ) : offerPaymentSummary ? (
                <div className="bg-zinc-800/30 rounded-xl p-5 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Servicio ofrecido</span>
                    <span className="text-white font-medium">
                      ${offerPaymentSummary.service_amount?.toLocaleString()} ARS
                    </span>
                  </div>
                  
                  {offerPaymentSummary.platform_fee > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Costo de servicio de plataforma</span>
                      <span className="text-yellow-400 font-medium">
                        ${offerPaymentSummary.platform_fee?.toLocaleString()} ARS
                      </span>
                    </div>
                  )}

                  <div className="border-t border-zinc-700 pt-4 flex justify-between items-center">
                    <span className="text-white font-bold text-lg">Total a pagar</span>
                    <span className="text-green-400 font-black text-2xl">
                      ${offerPaymentSummary.total_to_pay?.toLocaleString()}
                      <span className="text-zinc-500 text-sm font-normal ml-1">ARS</span>
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Info Note */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium">Pago seguro con MercadoPago</p>
                  <p className="text-zinc-400 mt-1">
                    El contrato se activa una vez confirmado el pago. El medio será notificado para comenzar el trabajo.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowOfferCheckoutModal(false);
                    setSelectedOfferForPayment(null);
                    setOfferPaymentSummary(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-[#009ee3] hover:bg-[#008ac9] text-white"
                  onClick={confirmOfferPayment}
                  disabled={processingOfferPayment || loadingOfferSummary}
                >
                  {processingOfferPayment ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <CreditCard size={16} className="mr-2" />
                  )}
                  Pagar con MercadoPago
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chat Modal */}
      <Dialog open={showChatModal} onOpenChange={setShowChatModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl h-[600px] p-0">
          {selectedContractForChat && (
            <ContractChat 
              contract={selectedContractForChat}
              userType="producer"
              onClose={() => setShowChatModal(false)}
              onContractUpdate={() => {
                fetchContracts();
                // Also refresh the selected contract
                api.get(`/press/contracts/${selectedContractForChat.id}`).then(res => {
                  setSelectedContractForChat(res.data);
                }).catch(console.error);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Portfolio Detail Modal */}
      <Dialog open={showPortfolioModal} onOpenChange={setShowPortfolioModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <ImageIcon size={20} className="text-yellow-400" />
              Trabajo del Portfolio
            </DialogTitle>
          </DialogHeader>
          
          {selectedPortfolioItem && selectedPressForPortfolio && (
            <div className="space-y-5 py-2">
              {/* Press Info */}
              <div className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {selectedPressForPortfolio.logo_url ? (
                    <img src={selectedPressForPortfolio.logo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="text-purple-400" size={20} />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedPressForPortfolio.media_name}</p>
                  <p className="text-zinc-500 text-xs">{selectedPressForPortfolio.media_type}</p>
                </div>
              </div>

              {/* Portfolio Image */}
              {selectedPortfolioItem.image_url && (
                <div className="relative rounded-xl overflow-hidden bg-zinc-800 aspect-video">
                  <img 
                    src={selectedPortfolioItem.image_url} 
                    alt={selectedPortfolioItem.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Title and Description */}
              <div>
                <h3 className="text-white font-bold text-xl mb-2">{selectedPortfolioItem.title}</h3>
                {selectedPortfolioItem.description && (
                  <p className="text-zinc-400 text-sm">{selectedPortfolioItem.description}</p>
                )}
              </div>

              {/* Event and Producer Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedPortfolioItem.event_name && (
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs mb-1">Evento</p>
                    <p className="text-white font-medium">{selectedPortfolioItem.event_name}</p>
                  </div>
                )}
                {selectedPortfolioItem.producer_name && (
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs mb-1">Productora</p>
                    <p className="text-white font-medium">{selectedPortfolioItem.producer_name}</p>
                  </div>
                )}
              </div>

              {/* Metrics */}
              {selectedPortfolioItem.metrics && (
                <div className="grid grid-cols-4 gap-3">
                  {selectedPortfolioItem.metrics.views > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <Eye className="text-blue-400 mx-auto mb-1" size={18} />
                      <p className="text-white font-bold">{formatNumber(selectedPortfolioItem.metrics.views)}</p>
                      <p className="text-zinc-500 text-xs">Vistas</p>
                    </div>
                  )}
                  {selectedPortfolioItem.metrics.reach > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <Users className="text-green-400 mx-auto mb-1" size={18} />
                      <p className="text-white font-bold">{formatNumber(selectedPortfolioItem.metrics.reach)}</p>
                      <p className="text-zinc-500 text-xs">Alcance</p>
                    </div>
                  )}
                  {selectedPortfolioItem.metrics.likes > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <Heart className="text-red-400 mx-auto mb-1" size={18} />
                      <p className="text-white font-bold">{formatNumber(selectedPortfolioItem.metrics.likes)}</p>
                      <p className="text-zinc-500 text-xs">Likes</p>
                    </div>
                  )}
                  {selectedPortfolioItem.metrics.shares > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                      <ExternalLink className="text-purple-400 mx-auto mb-1" size={18} />
                      <p className="text-white font-bold">{formatNumber(selectedPortfolioItem.metrics.shares)}</p>
                      <p className="text-zinc-500 text-xs">Compartidos</p>
                    </div>
                  )}
                </div>
              )}

              {/* Original URL */}
              {selectedPortfolioItem.url && (
                <a 
                  href={selectedPortfolioItem.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm bg-yellow-500/10 rounded-lg p-3"
                >
                  <ExternalLink size={16} />
                  Ver publicación original
                </a>
              )}

              {/* Navigation buttons if there's more than one item */}
              {selectedPressForPortfolio.portfolio && selectedPressForPortfolio.portfolio.length > 1 && (
                <div className="flex justify-between items-center pt-3 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const currentIndex = selectedPressForPortfolio.portfolio.findIndex(p => p.id === selectedPortfolioItem.id);
                      const prevIndex = currentIndex > 0 ? currentIndex - 1 : selectedPressForPortfolio.portfolio.length - 1;
                      setSelectedPortfolioItem(selectedPressForPortfolio.portfolio[prevIndex]);
                    }}
                  >
                    ← Anterior
                  </Button>
                  <span className="text-zinc-500 text-sm">
                    {selectedPressForPortfolio.portfolio.findIndex(p => p.id === selectedPortfolioItem.id) + 1} / {selectedPressForPortfolio.portfolio.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const currentIndex = selectedPressForPortfolio.portfolio.findIndex(p => p.id === selectedPortfolioItem.id);
                      const nextIndex = currentIndex < selectedPressForPortfolio.portfolio.length - 1 ? currentIndex + 1 : 0;
                      setSelectedPortfolioItem(selectedPressForPortfolio.portfolio[nextIndex]);
                    }}
                  >
                    Siguiente →
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ProducerLayout>
  );
};

export default ProducerPressPage;
